import { LocalStorageKeys } from '@/constants/local-storage';
import { useAlerts } from '@/hooks/alerts';
import { useEvents } from '@/hooks/events';
import { useWallet } from '@/hooks/wallet';
import { appPointsAtom, appSignatureAtom, Events } from '@/state/app';
import { Maybe } from '@/utils/common';
import { post } from '@/utils/http/instance-api';
import { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useRecoilValue } from 'recoil';
import { MissionHubTask } from '../MissionHubTask';

export interface TwitterLikesProps {
    signup: () => void;
}

export interface CurrentTweetResponse {
    _id: number;
    link: string;
    points: number;
    account: string;
    text: string;
    date: number;
    expired: boolean;
}

export const TwitterLikes = (props: TwitterLikesProps) => {
    const [cached, setCached] = useLocalStorage<number | null>(LocalStorageKeys.MISSION_HUB_LASTLIKED, null);
    const { pushEvent } = useEvents();
    const { alertSuccess, alertError } = useAlerts();
    const { walletState } = useWallet();
    const { standingData, twitterLimit } = useRecoilValue(appPointsAtom);
    const signature = useRecoilValue(appSignatureAtom);
    const [tweet, setTweet] = useState<Maybe<CurrentTweetResponse>>(null);
    const isSigned = walletState.selectedAddress && signature.address === walletState.selectedAddress;
    const awardedPoints = standingData.activities['TWITTER-MISSION']?.points;

    // Load initial quiz
    useEffect(() => {
        (async () => {
            try {
                const response = await post('/current_tweet');
                setTweet(response);
            } catch (e) {}
        })();
    }, []);

    const claimPoints = async () => {
        if (!isSigned) {
            await props.signup();
            return;
        }
        try {
            const response = await post('/twitter_like', signature);
            switch (response.code) {
                case 200:
                    setCached(tweet?._id!);
                    alertSuccess('Points claims successfully');
                    pushEvent(Events.refreshPoints);
                    break;
                case 401:
                    alertError('Please complete the connect Twitter account mission');
                    break;
                case 405:
                    alertError(
                        `Several requests are being processed. Please come back in ${
                            twitterLimit?.like?.nextMinutes ?? 20
                        } minutes and try again.`,
                    );
                    break;
                case 406:
                    alertError('Please like the tweet first');
                    break;
                case 407:
                    alertError('You can no longer get points for liking this tweet');
                    break;
                case 0:
                    alertError('Something went wrong');
                    break;
            }
        } catch (e) {
            alertError(
                `Several requests are being processed. Please come back in ${
                    twitterLimit?.like?.nextMinutes ?? 20
                } minutes and try again.`,
            );
        }
    };

    return (
        <MissionHubTask
            mission={{
                _id: 'TWITTER-MISSION',
                title: 'Latest Tweet by ClayStack',
                description:
                    tweet?.expired || !tweet?.text
                        ? 'Follow ClayStack on Twitter and like the tweets we post to gain points!'
                        : tweet?.text.slice(0, 120) + '...',
                timestamp: Date.now(),
            }}
            points={awardedPoints}
            onClick={claimPoints}
            isDisabled={!tweet || tweet?.expired || cached === tweet._id || twitterLimit.like?.rate_limited}
            ctaLabel={
                tweet?.expired
                    ? 'Look out for the next tweet'
                    : twitterLimit.like?.rate_limited
                    ? `Available in ${twitterLimit?.like?.nextMinutes ?? 20} mins`
                    : 'Liked it!'
            }
        />
    );
};
